import { useQuery } from '@apollo/client';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import EDDialog from '../../../../BaseComponents/Dialog/EDDialog';
import EDDivider from '../../../../BaseComponents/Divider/EDDivider';
import EDBox from '../../../../BaseComponents/EDBox/EDBox';
import EDStandardSelect from '../../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';
import { ActiveDatabaseContext } from '../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import GroupHeader from '../../_Common/Components/BusinessCollectionComponents/GroupHeader/GroupHeader';
import {
  BisattningsRumDelPlacering,
  BisattningsRumDelPlaceringVariables,
} from '../Queries/_QueryTypes/BisattningsRumDelPlacering';
import bisattningsRumDelPlaceringQuery from '../Queries/BisattningsRumDelPlacering.gql';
import { IBusinessDataFormContext } from '../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { PlaceringGrid } from './PlaceringGrid';

interface IProps {
  formContext: IBusinessDataFormContext<any, unknown>;
  directUpdate?: boolean;
  open: boolean;
  lokalID: number;
  typ: string;
  onClose: () => void;
  readOnly?: boolean;
  existingPlacering?: {
    rumBen?: string;
    placeringBen?: string;
    placeringID?: string;
  };
}

const ValjPlacering: React.FunctionComponent<IProps> = ({
  formContext,
  directUpdate = false,
  open,
  lokalID,
  typ,
  onClose,
  readOnly = false,
  existingPlacering,
}) => {
  const [room, setRoom] = useState<number | undefined>(undefined);
  const [placeringInfo, setPlaceringInfo] = useState<{
    rumBen: string | undefined;
    delBen: string | undefined;
    placeringID: string | undefined;
    placeringBen: string | undefined;
  }>({
    rumBen: undefined,
    delBen: undefined,
    placeringID: undefined,
    placeringBen: undefined,
  });
  const [previousRoom, setPreviousRoom] = useState<number | undefined>(
    undefined
  );
  const [previousPlaceringInfo, setPreviousPlaceringInfo] =
    useState<typeof placeringInfo>(placeringInfo);
  const [previousGridSizes, setPreviousGridSizes] = useState<
    { rows: number; columns: number }[]
  >([]);
  const [gridSizes, setGridSizes] = useState<
    { rows: number; columns: number }[]
  >([]);

  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const dbName = activeDatabaseContext.activeDatabase ?? '';

  const { data: bisattningRum } = useQuery<
    BisattningsRumDelPlacering,
    BisattningsRumDelPlaceringVariables
  >(bisattningsRumDelPlaceringQuery, {
    variables: {
      dbname: dbName,
      lokalID: lokalID.toString(),
      typ: [{ value: typ }],
    },
    fetchPolicy: 'network-only',
  });

  const rooms = useMemo(
    () =>
      bisattningRum?.db?.BOBisattningsrum_All?.map((rum, index) => ({
        header: rum.BENAMNING,
        value: index,
        delar: rum._Associations?.Del_All || [],
      })) || [],
    [bisattningRum?.db?.BOBisattningsrum_All]
  );

  // Use useEffect to update gridSizes when room changes
  useEffect(() => {
    if (room !== undefined) {
      const selectedRoom = rooms[room];
      if (selectedRoom?.delar.length) {
        const sizes = selectedRoom.delar.map(del => ({
          rows: del.RADER || 0,
          columns: del.KOLUMNER || 0,
        }));
        setGridSizes(sizes);
      } else {
        setGridSizes([]);
      }
    }
  }, [room, rooms]);

  // Separate useEffect for handling existingPlacering
  useEffect(() => {
    if (readOnly && existingPlacering && rooms.length > 0) {
      const roomIndex = rooms.findIndex(
        r => r.header === existingPlacering.rumBen
      );
      if (roomIndex !== -1) {
        setRoom(roomIndex);
        setPlaceringInfo({
          rumBen: existingPlacering.rumBen,
          delBen: undefined,
          placeringID: existingPlacering.placeringID,
          placeringBen: existingPlacering.placeringBen,
        });
      }
    }
  }, [readOnly, existingPlacering, rooms]);

  const handleOpen = () => {
    setPreviousRoom(room);
    setPreviousPlaceringInfo(placeringInfo);
    setPreviousGridSizes(gridSizes);
  };

  const handleClose = () => {
    setRoom(previousRoom);
    setPlaceringInfo(previousPlaceringInfo);
    setGridSizes(previousGridSizes);
    onClose();
  };

  const handleRumChange = (selected: number) => {
    setRoom(selected);
    const selectedRoom = rooms[selected];
    setPlaceringInfo({
      rumBen: selectedRoom?.header || '',
      delBen: undefined,
      placeringID: undefined,
      placeringBen: undefined,
    });
  };
  const footerActions = readOnly
    ? [
        {
          label: 'Stäng',
          onClick: handleClose,
        },
      ]
    : [
        {
          label: 'Avbryt',
          onClick: handleClose,
        },
        {
          label: 'Välj markerad placering',
          onClick: () => {
            formContext?.update([
              {
                operation: 'Values',
                associationName: 'SenasteHandelse',
                values: {
                  PLACERING_EJ_MALL: placeringInfo.rumBen
                    ? placeringInfo.placeringBen
                      ? placeringInfo.rumBen + ' ' + placeringInfo.placeringBen
                      : placeringInfo.rumBen
                    : '',
                  PLACERING_ID: placeringInfo.placeringID,
                },
              },
            ]);
            onClose();
          },
          buttonProps: {
            disabled:
              placeringInfo === previousPlaceringInfo ||
              (!!rooms[room ?? -1]?.delar.length && !placeringInfo.placeringID),
          },
        },
      ];
  return (
    <EDDialog
      title="Välj plats"
      open={open}
      onClose={handleClose}
      onEnter={handleOpen}
      maxWidth={false}
      footerActions={footerActions}
    >
      <GroupHeader>Välj placering</GroupHeader>
      <EDBox>
        <EDStandardSelect<number>
          label="Välj rum"
          onChange={e => {
            if (!readOnly) {
              handleRumChange(e);
            }
          }}
          value={room}
          items={rooms}
          disabled={readOnly}
        />
      </EDBox>
      <EDDivider marginBottom={3} />
      <PlaceringGrid
        formContext={formContext}
        directUpdate={directUpdate}
        gridSizes={gridSizes}
        rooms={rooms}
        room={room}
        placeringInfo={placeringInfo}
        setPlaceringInfo={setPlaceringInfo}
      />
    </EDDialog>
  );
};

export default ValjPlacering;
